/**
 * Quantities: exact decimals (never JS floats). A `Qty` is a decimal.js value with a brand;
 * on the wire, in commands and in DB params a quantity is a canonical decimal **string**.
 * @module
 */
import Decimal from 'decimal.js';
import { z } from 'zod';
import { D, type RoundingMode, decimalRounding } from '../internal/decimal.js';

export type { RoundingMode } from '../internal/decimal.js';

declare const qtyBrand: unique symbol;

/** An exact decimal quantity. Create with {@link qty}; serialise with {@link qtyToString}. */
export type Qty = Decimal & { readonly [qtyBrand]: true };

/** Anything {@link qty} accepts: a decimal string, an integer number, or a Decimal/Qty. */
export type QtyInput = string | number | Decimal;

/** Plain decimal notation only: optional minus, digits, optional fraction. No exponent. */
export const QTY_STRING_RE = /^-?\d+(?:\.\d+)?$/;

function brand(d: Decimal): Qty {
  return (d.isZero() ? new D(0) : d) as Qty; // normalise −0
}

/**
 * Parses a quantity. Strings must be plain decimals (`"1.5"`, `"-2"`, `"0.125"`).
 * Numbers must be safe integers: a JS float like `0.1` is rejected on purpose, because it
 * has already lost precision. Throws `RangeError` on bad input.
 */
export function qty(input: QtyInput): Qty {
  if (typeof input === 'string') {
    if (!QTY_STRING_RE.test(input)) {
      throw new RangeError(`Not a decimal quantity string: ${JSON.stringify(input)}`);
    }
    return brand(new D(input));
  }
  if (typeof input === 'number') {
    if (!Number.isSafeInteger(input)) {
      throw new RangeError(
        `Quantities from JS numbers must be safe integers (got ${input}); pass a decimal string`,
      );
    }
    return brand(new D(input));
  }
  if (Decimal.isDecimal(input) && input.isFinite()) return brand(new D(input));
  throw new RangeError('Not a finite quantity');
}

/** True when `text` is a valid quantity string. */
export function isQtyString(text: string): boolean {
  return QTY_STRING_RE.test(text);
}

/** Canonical string: plain notation, no exponent, no trailing zeros, no `-0`. */
export function qtyToString(value: Qty): string {
  return value.toFixed();
}

/** a + b, exact. */
export function addQty(a: QtyInput, b: QtyInput): Qty {
  return brand(qty(a).plus(qty(b)));
}

/** a − b, exact. */
export function subQty(a: QtyInput, b: QtyInput): Qty {
  return brand(qty(a).minus(qty(b)));
}

/** Sum of many quantities, exact. Empty → 0. */
export function sumQty(values: Iterable<QtyInput>): Qty {
  let total = new D(0);
  for (const v of values) total = total.plus(qty(v));
  return brand(total);
}

/** Rounds to `dp` decimal places with an explicit mode. */
export function roundQty(value: QtyInput, dp: number, mode: RoundingMode = 'half_up'): Qty {
  if (!Number.isSafeInteger(dp) || dp < 0) throw new RangeError('dp must be an integer >= 0');
  return brand(qty(value).toDecimalPlaces(dp, decimalRounding(mode)));
}

/** Precision for {@link multiplyQty} and {@link divideQty}. */
export interface QtyPrecision {
  /** Decimal places to round the result to. */
  dp: number;
  /** Rounding mode (default `half_up`). */
  rounding?: RoundingMode;
}

/** a × b. Exact unless `precision` is given, in which case it is rounded to `dp`. */
export function multiplyQty(a: QtyInput, b: QtyInput, precision?: QtyPrecision): Qty {
  const product = brand(qty(a).times(qty(b)));
  return precision ? roundQty(product, precision.dp, precision.rounding) : product;
}

/**
 * a ÷ b, rounded to `precision.dp` decimal places. Division needs explicit precision
 * because 1 ÷ 3 has no exact decimal. Throws on division by zero.
 */
export function divideQty(a: QtyInput, b: QtyInput, precision: QtyPrecision): Qty {
  const divisor = qty(b);
  if (divisor.isZero()) throw new RangeError('Division by zero');
  const quotient = qty(a).div(divisor);
  return roundQty(quotient, precision.dp, precision.rounding);
}

/** −a. */
export function negQty(a: QtyInput): Qty {
  return brand(qty(a).negated());
}

/** |a|. */
export function absQty(a: QtyInput): Qty {
  return brand(qty(a).abs());
}

/** −1, 0 or 1 as a is less than, equal to or greater than b. */
export function compareQty(a: QtyInput, b: QtyInput): -1 | 0 | 1 {
  return qty(a).comparedTo(qty(b)) as -1 | 0 | 1;
}

/** Numeric equality (`"1.50"` equals `"1.5"`). */
export function qtyEquals(a: QtyInput, b: QtyInput): boolean {
  return compareQty(a, b) === 0;
}

/** The smaller of two quantities. */
export function minQty(a: QtyInput, b: QtyInput): Qty {
  return compareQty(a, b) <= 0 ? qty(a) : qty(b);
}

/** The larger of two quantities. */
export function maxQty(a: QtyInput, b: QtyInput): Qty {
  return compareQty(a, b) >= 0 ? qty(a) : qty(b);
}

/** True when the quantity is zero. */
export function isZero(a: QtyInput): boolean {
  return qty(a).isZero();
}

/** True when the quantity is below zero. */
export function isNegative(a: QtyInput): boolean {
  const v = qty(a);
  return v.isNegative() && !v.isZero();
}

/** True when the quantity is above zero. */
export function isPositive(a: QtyInput): boolean {
  const v = qty(a);
  return v.isPositive() && !v.isZero();
}

/** The number of decimal places in the canonical form. */
export function decimalPlaces(a: QtyInput): number {
  return qty(a).decimalPlaces();
}

/** zod schema for a quantity on the wire: a plain decimal string (may be negative). */
export const qtyString = z
  .string()
  .regex(QTY_STRING_RE, 'Expected a decimal quantity string, e.g. "1.5"');

// The refinements below guard on the pattern: zod runs them even when the regex failed, and
// the helpers throw on a malformed string (a 500 instead of a validation error).

/** zod schema: a quantity string that is > 0. */
export const positiveQtyString = qtyString.refine(
  (s) => isQtyString(s) && isPositive(s),
  'Must be greater than 0',
);

/** zod schema: a quantity string that is ≥ 0. */
export const nonNegativeQtyString = qtyString.refine(
  (s) => isQtyString(s) && !isNegative(s),
  'Must not be negative',
);
